// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuthSession } from "../hooks/useAuthSession";
import { useRestartSignIn } from "../hooks/useRestartSignIn";
import { isDevPreviewMode } from "../utils/devEnv";
import { isTestMode } from "../utils/runtimeEnv";
import { isDemoWorkspaceTenant } from "../utils/demoWorkspace";
import { getTenantSettings } from "../features/onboarding/api/tenantSettingsApi";
import type { TenantSettingsModel } from "../features/onboarding/models/onboardingModels";
import { ApiServiceError } from "../services/api/apiError";
import {
  COLD_START_RETRY_BUDGET_MS,
  WAKING_UP_AFTER_MS,
  type WarmUpFailure,
  classifyWarmUpFailure,
  isColdStartRetryable,
  retryColdStart,
} from "../services/api/coldStartRetry";
import {
  isSubscriptionActive,
  type SubscriptionStatusValue,
} from "../features/billing/models/subscriptionModels";

interface SubscriptionContextValue {
  isActive: boolean;
  isLoading: boolean;
  isWakingUp: boolean;
  status: SubscriptionStatusValue;
  readFailure: WarmUpFailure | undefined;
  retry: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(
  undefined,
);

const DEMO_STATUS: SubscriptionStatusValue = "active";

export const SUBSCRIPTION_READ_BUDGET_MS = COLD_START_RETRY_BUDGET_MS;
export const SUBSCRIPTION_ATTEMPT_TIMEOUT_MS = 15_000;
export const SUBSCRIPTION_WAKING_UP_AFTER_MS = WAKING_UP_AFTER_MS;

export const SUBSCRIPTION_RECOVERY_RETRY_MS = 2_500;
export const SUBSCRIPTION_RECOVERY_RETRY_LIMIT = 20;

export class SubscriptionReadTimeoutError extends Error {
  constructor() {
    super("The subscription check did not finish in time.");
    this.name = "SubscriptionReadTimeoutError";
  }
}

function isGateOpen(settings: TenantSettingsModel): boolean {
  const status = (settings.settings.subscription?.status ??
    "no_subscription") as SubscriptionStatusValue;
  const isDemoWorkspace = isDemoWorkspaceTenant(settings.tenantId);
  return isSubscriptionActive(status) || isDemoWorkspace;
}

function isTenantNotCreatedYet(error: unknown): boolean {
  return error instanceof ApiServiceError && error.status === 404;
}

export function isUnauthenticatedReadError(error: unknown): boolean {
  return error instanceof ApiServiceError && error.status === 401;
}

export function isTransientReadError(error: unknown): boolean {
  return error instanceof SubscriptionReadTimeoutError || isColdStartRetryable(error);
}

function untilAborted<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => reject(new SubscriptionReadTimeoutError());
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener("abort", onAbort, { once: true });
    work.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
}

async function readOnce(
  getAccessToken: () => Promise<string>,
  signal: AbortSignal,
): Promise<TenantSettingsModel> {
  const token = await untilAborted(getAccessToken(), signal);
  const firstRead = await untilAborted(getTenantSettings({ token, signal }), signal);
  return isGateOpen(firstRead)
    ? firstRead
    : await untilAborted(getTenantSettings({ token, refresh: true, signal }), signal);
}

async function readTenantSubscription(
  getAccessToken: () => Promise<string>,
  isCurrent: () => boolean,
  onSlow: () => void,
  retryTransient: boolean,
  cancel: AbortSignal,
): Promise<TenantSettingsModel> {
  const deadline = Date.now() + SUBSCRIPTION_READ_BUDGET_MS;
  const slowTimer = setTimeout(onSlow, SUBSCRIPTION_WAKING_UP_AFTER_MS);
  let controller = new AbortController();
  let capTimer: ReturnType<typeof setTimeout> | undefined;
  let capped = false;
  let stop: () => void = () => {};
  const hardCap = new Promise<never>((_resolve, reject) => {
    stop = (): void => {
      capped = true;
      controller.abort();
      reject(new SubscriptionReadTimeoutError());
    };
    capTimer = setTimeout(stop, SUBSCRIPTION_READ_BUDGET_MS);
  });
  const onCancel = (): void => stop();
  if (cancel.aborted) {
    onCancel();
  } else {
    cancel.addEventListener("abort", onCancel, { once: true });
  }
  try {
    return await Promise.race([hardCap, retryWithinBudget()]);
  } finally {
    cancel.removeEventListener("abort", onCancel);
    clearTimeout(slowTimer);
    clearTimeout(capTimer);
  }

  function retryWithinBudget(): Promise<TenantSettingsModel> {
    return retryColdStart(readAttempt, {
      deadline,
      signal: cancel,
      isRetryable: isTransientReadError,
      canRetry: () => !capped && retryTransient && isCurrent(),
      onRetry: onSlow,
    });
  }

  async function readAttempt(): Promise<TenantSettingsModel> {
    if (capped) {
      throw new SubscriptionReadTimeoutError();
    }
    controller = new AbortController();
    const attemptTimer = setTimeout(
      () => controller.abort(),
      Math.max(0, Math.min(SUBSCRIPTION_ATTEMPT_TIMEOUT_MS, deadline - Date.now())),
    );
    try {
      return await readOnce(getAccessToken, controller.signal);
    } catch (error) {
      throw controller.signal.aborted ? new SubscriptionReadTimeoutError() : error;
    } finally {
      clearTimeout(attemptTimer);
    }
  }
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { getAccessToken, isAuthenticated, isConfigured } = useAuthSession();
  const restartSignIn = useRestartSignIn();
  const mountedRef = useRef(true);
  const loadSequenceRef = useRef(0);
  const loadControllerRef = useRef<AbortController | undefined>(undefined);
  const quietLoadRef = useRef<Promise<void> | undefined>(undefined);
  const quietControllerRef = useRef<AbortController | undefined>(undefined);

  const [value, setValue] = useState<Omit<SubscriptionContextValue, "retry">>({
    isActive: true,
    isLoading: true,
    isWakingUp: false,
    status: "no_subscription",
    readFailure: undefined,
  });

  const runLoad = useCallback(
    async (quiet: boolean, cancel: AbortSignal): Promise<void> => {
      if (isTestMode() || isDevPreviewMode) {
        setValue({
          isActive: true,
          isLoading: false,
          isWakingUp: false,
          status: DEMO_STATUS,
          readFailure: undefined,
        });
        return;
      }

      if (!isConfigured) {
        setValue({
          isActive: false,
          isLoading: false,
          isWakingUp: false,
          status: "unverified",
          readFailure: undefined,
        });
        return;
      }

      if (!quiet && mountedRef.current) {
        setValue((previous) => ({ ...previous, isLoading: true, isWakingUp: false }));
      }

      if (!quiet) {
        loadSequenceRef.current += 1;
      }
      const sequence = loadSequenceRef.current;
      const isCurrent = (): boolean =>
        mountedRef.current && sequence === loadSequenceRef.current;
      const onSlow = (): void => {
        if (!quiet && isCurrent()) {
          setValue((previous) =>
            previous.isLoading && !previous.isWakingUp
              ? { ...previous, isWakingUp: true }
              : previous,
          );
        }
      };
      try {
        const tenantSettings = await readTenantSubscription(
          getAccessToken,
          isCurrent,
          onSlow,
          !quiet,
          cancel,
        );
        if (!isCurrent()) {
          return;
        }
        const status = (tenantSettings.settings.subscription?.status ??
          "no_subscription") as SubscriptionStatusValue;
        const isDemoWorkspace = isDemoWorkspaceTenant(tenantSettings.tenantId);
        const active = isSubscriptionActive(status) || isDemoWorkspace;
        if (quiet) {
          setValue((previous) => ({ ...previous, isActive: active, status, readFailure: undefined }));
        } else {
          setValue({
            isActive: active,
            isLoading: false,
            isWakingUp: false,
            status,
            readFailure: undefined,
          });
        }
      } catch (error) {
        if (!isCurrent()) {
          return;
        }

        if (isTenantNotCreatedYet(error)) {
          setValue({
            isActive: true,
            isLoading: false,
            isWakingUp: false,
            status: "no_subscription",
            readFailure: undefined,
          });
          return;
        }

        if (quiet) {
          if (isUnauthenticatedReadError(error)) {
            setValue((previous) => ({ ...previous, isLoading: true, readFailure: undefined }));
            void restartSignIn().catch(() => {
            });
          }
          return;
        }

        console.warn("Subscription read failed", error);
        setValue({
          isActive: false,
          isLoading: false,
          isWakingUp: false,
          status: "unverified",
          readFailure: classifyWarmUpFailure(error, isTransientReadError),
        });
      }
    },
    [getAccessToken, isConfigured, restartSignIn],
  );

  const load = useCallback(
    (quiet: boolean): Promise<void> => {
      if (quiet) {
        if (quietLoadRef.current !== undefined) {
          return quietLoadRef.current;
        }
        const controller = new AbortController();
        quietControllerRef.current = controller;
        const pending = runLoad(true, controller.signal).finally(() => {
          if (quietLoadRef.current === pending) {
            quietLoadRef.current = undefined;
            quietControllerRef.current = undefined;
          }
        });
        quietLoadRef.current = pending;
        return pending;
      }
      loadControllerRef.current?.abort();
      const controller = new AbortController();
      loadControllerRef.current = controller;
      return runLoad(false, controller.signal).finally(() => {
        if (loadControllerRef.current === controller) {
          loadControllerRef.current = undefined;
        }
      });
    },
    [runLoad],
  );

  useEffect(() => {
    mountedRef.current = true;

    const neverGated = isTestMode() || isDevPreviewMode;
    if (!neverGated && !isAuthenticated) {
      return () => {
        mountedRef.current = false;
      };
    }

    void load(false);

    const onFocus = (): void => {
      void load(true);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        void load(true);
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mountedRef.current = false;
      loadControllerRef.current?.abort();
      quietControllerRef.current?.abort();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load, isAuthenticated, isConfigured]);

  useEffect(() => {
    if (value.readFailure === undefined || value.readFailure === "signedOut") {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (): void => {
      timer = setTimeout(() => {
        void (async () => {
          if (cancelled) {
            return;
          }
          await load(true);
          attempts += 1;
          if (!cancelled && attempts < SUBSCRIPTION_RECOVERY_RETRY_LIMIT) {
            schedule();
          }
        })();
      }, SUBSCRIPTION_RECOVERY_RETRY_MS);
    };

    schedule();

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [value.readFailure, load]);

  const retry = useCallback((): void => {
    void load(false);
  }, [load]);

  const contextValue = useMemo(
    () => ({ ...value, retry }),
    [value, retry],
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionStatus(): SubscriptionContextValue {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscriptionStatus must be used within SubscriptionProvider",
    );
  }
  return context;
}
